/**
 * Article page behaviour for sections/arvana-article.liquid:
 * - highlights the "In This Article" link of the heading being read
 * - copies the article link from the share button
 * - reports `arvana_article_read` once the reader passes 75% of the body
 * Analytics go through the `arvana:track` event, which assets/arvana-analytics.js publishes.
 */

/**
 * @param {string} name
 * @param {Record<string, unknown>} data
 */
function track(name, data) {
  document.dispatchEvent(new CustomEvent('arvana:track', { detail: { name, data } }));
}

function setupToc() {
  const links = /** @type {HTMLAnchorElement[]} */ ([...document.querySelectorAll('.arvana-article__toc a[href^="#"]')]);
  if (!links.length) return;

  const headings = links
    .map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
    .filter((heading) => heading instanceof HTMLElement);

  /** @param {string} id */
  const setActive = (id) => {
    for (const link of links) {
      link.setAttribute('aria-current', String(link.hash.slice(1) === id));
    }
  };

  // The active heading is the last one above the upper third of the viewport
  const update = () => {
    const line = window.innerHeight / 3;
    let active = headings[0];
    for (const heading of headings) {
      if (heading.getBoundingClientRect().top <= line) active = heading;
    }
    if (active) setActive(active.id);
  };

  const observer = new IntersectionObserver(update, { rootMargin: '0px 0px -66% 0px' });
  headings.forEach((heading) => observer.observe(heading));
  update();
}

function setupCopy() {
  for (const button of document.querySelectorAll('[data-arvana-copy]')) {
    button.addEventListener('click', async () => {
      if (!(button instanceof HTMLElement)) return;
      const status = button.closest('.arvana-article__share')?.querySelector('.arvana-article__copied');
      try {
        await navigator.clipboard.writeText(button.dataset.arvanaCopy ?? window.location.href);
      } catch {
        return;
      }
      if (!(status instanceof HTMLElement)) return;
      status.textContent = button.dataset.copiedLabel ?? '';
      status.hidden = false;
      setTimeout(() => {
        status.hidden = true;
      }, 2500);
    });
  }
}

function setupReadTracking() {
  const body = document.querySelector('[data-arvana-article]');
  const marker = body?.querySelector('.arvana-article__read-marker');
  if (!(body instanceof HTMLElement) || !marker) return;

  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    track('arvana_article_read', {
      label: body.dataset.arvanaArticle ?? document.title,
      tags: body.dataset.arvanaTags ?? '',
      url: window.location.href,
    });
  });
  observer.observe(marker);
}

setupToc();
setupCopy();
setupReadTracking();
